<template>
  <transition name="slide-fade">
    <div v-if="isVisible" class="csat-popup-container">

      <div class="csat-card">

        <button class="close-btn" @click="handleDismiss" title="Fechar pesquisa">
          ✕
        </button>

        <div v-if="!voted" class="csat-content">
          <div class="header">
            <span class="icon">💬</span>
            <p class="question-text">{{ currentQuestionText }}</p>
          </div>

          <div class="rating-options">
            <button
                v-for="score in 5"
                :key="score"
                class="rating-btn"
                :class="getScoreClass(score)"
                @click="handleVote(score)"
            >
              <span class="score-number">{{ score }}</span>
              <span class="score-label">{{ getScoreLabel(score) }}</span>
            </button>
          </div>
        </div>

        <div v-else class="csat-thanks">
          <div class="success-icon">✓</div>
          <p>Obrigado por compartilhar sua opinião!</p>
          <small>Isso nos ajuda a melhorar cada vez mais.</small>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useCsat } from '@/services/csat.service';
import { useLaboratoryStore } from '@/stores/laboratoryStore';

const { isVisible, stage, submitVote, dismissPopup, checkCsatStatus } = useCsat();
const laboratoryStore = useLaboratoryStore();

const voted = ref(false);

const laboratoryId = computed(() => laboratoryStore.currentLab?.id);

const currentQuestionText = computed(() => {
  return stage.value === 1
      ? "Conte para nós! Você está satisfeito com o Laboratório Digital até agora?"
      : "Sua opinião vale muito! Como está sendo sua experiência com o Laboratório Digital?";
});

const getScoreLabel = (score) => {
  const labels = ["Muito insatisfeito", "Insatisfeito", "Neutro", "Satisfeito", "Muito satisfeito"];
  return labels[score - 1];
};

const getScoreClass = (score) => {
  if (score <= 2) return 'score-low';
  if (score === 3) return 'score-mid';
  return 'score-high';
};

const handleVote = async (score) => {
  const success = await submitVote(score, laboratoryId.value);

  if (success) {
    voted.value = true;

    setTimeout(() => {
      isVisible.value = false;
      setTimeout(() => { voted.value = false; }, 500);
    }, 3000);
  }
};

const handleDismiss = () => {
  dismissPopup(laboratoryId.value);
};

onMounted(() => {
  if (laboratoryId.value) {
    checkCsatStatus(laboratoryId.value);
  }
});

watch(laboratoryId, (newId) => {
  if (newId) {
    checkCsatStatus(newId);
  }
});
</script>

<style scoped>
.csat-popup-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 99999;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.csat-card {
  background: #ffffff;
  width: 320px;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06);
  padding: 20px;
  border-left: 6px solid #1A5FD5;
  position: relative;
  bottom: 45px;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 18px;
  color: #9CA3AF;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.header {
  margin-bottom: 16px;
}

.icon {
  font-size: 20px;
  display: block;
  margin-bottom: 8px;
}

.question-text {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  line-height: 1.4;
  margin: 0;
}

.rating-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rating-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background-color: #F9FAFB;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.rating-btn:hover {
  transform: translateX(4px);
  background-color: #F3F4F6;
  border-color: #D1D5DB;
}

.score-number {
  background: #E5E7EB;
  color: #374151;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
}

.score-label {
  font-size: 13px;
  color: #4B5563;
}

.rating-btn.score-low:hover .score-number {
  background: #FCA5A5;
  color: #7F1D1D;
}

.rating-btn.score-mid:hover .score-number {
  background: #FCD34D;
  color: #78350F;
}

.rating-btn.score-high:hover .score-number {
  background: #6EE7B7;
  color: #064E3B;
}

.csat-thanks {
  text-align: center;
  padding: 20px 0;
  animation: fadeIn 0.5s;
}

.success-icon {
  font-size: 32px;
  color: #10B981;
  margin-bottom: 12px;
}

.csat-thanks p {
  font-weight: 700;
  color: #065F46;
  margin: 0 0 4px 0;
}

.csat-thanks small {
  color: #6B7280;
}

.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px) scale(0.95);
  opacity: 0;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>